import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { CheckBox, Input, Label, Link } from '@iwoca/orion';
import { Trans, useLingui } from '@lingui/react/macro';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import styles from './EmailScreen.module.css';
import { emailSchema } from './EmailScreen.schema';
import { MobileSignupReferralCode } from './MobileSignupReferralCode/MobileSignupReferralCode';
import { useGetApplicationQuery } from '../../../../api/lendingApi/lendingApiQueries';
import { useSignupParams } from '../../../../hooks/useSignupParams';
import { getAmplitudeDefaultEventProperties } from '../../../../utils/amplitudeEvents';
import { DuplicateEmailError } from '../../../GetStarted/submitGetStarted';
import { useDuplicateEmail } from '../../../GetStarted/useDuplicateEmail';
import { TMobileSignupForm } from '../../form/form';
import { useStageFieldTracking } from '../../form/MobileSignupFieldTracking';
import { useMobileSignupAccountSetup } from '../../form/useMobileSignupAccountSetup';
import { useScreenValidation } from '../../form/validation/useScreenValidation';
import { StepLayout } from '../../StepLayout/StepLayout';

export const EmailScreen = () => {
  const { t } = useLingui();
  const {
    register,
    getValues,
    formState: { errors },
  } = useFormContext<TMobileSignupForm>();
  const { application } = useGetApplicationQuery();
  const { setUpAccount } = useMobileSignupAccountSetup();
  const {
    duplicateEmail,
    setDuplicateEmail,
    checkEmailOnBlur,
    checkEmailOnSubmit,
  } = useDuplicateEmail();
  const { isDirectCustomer } = useSignupParams();
  const { pathname, search } = useLocation();
  const basePath = isDirectCustomer ? '' : '/partner-signup';
  const linkToLogin = `/login/?redirect_to=${encodeURIComponent(
    `${basePath}${pathname}${search}`,
  )}`;

  const submitEmail = async () => {
    const form = getValues();
    if (!(await checkEmailOnSubmit(form.email))) return false;

    try {
      await setUpAccount(form);
    } catch (error) {
      if (!(error instanceof DuplicateEmailError)) throw error;

      tracking.ampli.duplicateEmail({
        ...getAmplitudeDefaultEventProperties(),
      });
      setDuplicateEmail({ email: error.email, status: true });
      return false;
    }
  };

  const { validateAndContinue, isSubmitting, submitError } =
    useScreenValidation(emailSchema, submitEmail);
  const { fieldProps } = useStageFieldTracking();
  const [isValidatingReferral, setIsValidatingReferral] = useState(false);

  return (
    <StepLayout
      stageTitle={t`Credit request`}
      heading={t`What is your business email address?`}
      subHeading={t`You can log in to this application with this email:`}
      ctaDisabled={isValidatingReferral}
      onContinue={validateAndContinue}
      loading={isSubmitting}
      submitError={submitError}
      fieldProps={fieldProps}
    >
      <div className={styles.emailContainer}>
        <Input
          {...register('email')}
          type="email"
          label={t`Email address`}
          placeholder={t`firstname@example.com`}
          error={errors.email?.message}
          disabled={Boolean(application)}
          className={clsx('fs-mask', {
            [styles.duplicateEmailError]: duplicateEmail.status,
          })}
          onBlur={() =>
            checkEmailOnBlur({
              email: getValues('email'),
              validateEmail: () => emailSchema.isValid(getValues()),
              assumeAvailable: Boolean(application),
            })
          }
          data-testid="email-input"
        />
        {duplicateEmail.status && !errors.email?.message && (
          <p
            data-testid="duplicate-email-message"
            className={styles.duplicateEmail}
          >
            <strong>
              <Trans>That email already exists.</Trans>{' '}
              <Link size="small" href={linkToLogin} rel="noopener noreferrer">
                <Trans>Log in</Trans>
              </Link>
            </strong>
          </p>
        )}
      </div>
      <MobileSignupReferralCode onValidatingChange={setIsValidatingReferral} />
      <div className={styles.marketingOptIn}>
        <CheckBox {...register('marketingOptIn')} id="marketingOptInId" />
        <Label
          className={styles.marketingOptInLabel}
          htmlFor="marketingOptInId"
        >
          <Trans>
            Sign up for offers and stories from other small businesses.
          </Trans>
        </Label>
      </div>
      <p className={styles.legal}>
        <Trans>
          I hereby consent to the processing of my personal data in accordance
          with our current{' '}
          <a
            href="https://www.iwoca.de/datenschutzhinweis/"
            target="_blank"
            rel="noreferrer"
          >
            privacy policy
          </a>{' '}
          of iwoca Deutschland GmbH. This consent can be withdrawn at any time.
          I agree to the{' '}
          <a
            href="https://www.iwoca.de/allgemeine_geschaeftsbedingungen/"
            target="_blank"
            rel="noreferrer"
          >
            terms and conditions
          </a>{' '}
          of iwoca Deutschland GmbH.
        </Trans>
      </p>
    </StepLayout>
  );
};
